import React, { useState } from 'react';
import { ExternalLink, Globe, Calendar, Newspaper, Mail, BookOpen, Bell, Users } from 'lucide-react';
import { dashboardTiles } from '../data/mock';
import { Card } from './ui/card';
import PushNotificationToggle from './PushNotificationToggle';

// Icon mapping
const iconMap = {
  Calendar,
  Newspaper,
  Mail,
  BookOpen,
  Bell,
  Users
};

const Dashboard = () => {
  const [imageErrors, setImageErrors] = useState({});

  const handleTileClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getFaviconUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`;
    } catch (e) {
      return null;
    }
  };

  const handleImageError = (tileId) => {
    setImageErrors(prev => ({ ...prev, [tileId]: true }));
  };

  const renderIcon = (tile) => {
    const hasError = imageErrors[tile.id];
    
    // Tiles that should NOT have white background
    const noWhiteBackgroundIds = ['7', '8', '12', '13', '14', '15', '16'];
    const shouldHaveWhiteBackground = !noWhiteBackgroundIds.includes(tile.id);
    
    // Tiles that should have bigger icons
    const biggerIconIds = ['12', '13', '14']; // High School+, Career Counselling Newsletter, myPass
    const iconSize = biggerIconIds.includes(tile.id) ? 'w-24 h-24' : 'w-16 h-16';

    // Priority 1: Custom uploaded icon
    if (tile.customIcon && !hasError) {
      if (shouldHaveWhiteBackground) {
        return (
          <div className={`${iconSize} rounded-lg bg-white flex items-center justify-center shadow-md p-2`}>
            <img
              src={tile.customIcon}
              alt={`${tile.title} icon`}
              className="w-full h-full object-contain"
              onError={() => handleImageError(tile.id)}
            />
          </div>
        );
      } else {
        return (
          <img
            src={tile.customIcon}
            alt={`${tile.title} icon`}
            className={`${iconSize} object-contain`}
            onError={() => handleImageError(tile.id)}
          />
        );
      }
    }

    // Priority 2: Lucide React icon
    if (tile.iconName && iconMap[tile.iconName]) {
      const IconComponent = iconMap[tile.iconName];
      return (
        <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center shadow-md">
          <IconComponent className="w-6 h-6 text-teal-600" />
        </div>
      );
    }

    // Priority 3: Favicon from website
    const faviconUrl = getFaviconUrl(tile.url);
    if (faviconUrl && !hasError) {
      if (shouldHaveWhiteBackground) {
        return (
          <div className={`${iconSize} rounded-lg bg-white flex items-center justify-center shadow-md p-2`}>
            <img
              src={faviconUrl}
              alt={`${tile.title} icon`}
              className="w-full h-full object-contain"
              onError={() => handleImageError(tile.id)}
            />
          </div>
        );
      } else {
        return (
          <img
            src={faviconUrl}
            alt={`${tile.title} icon`}
            className={`${iconSize} object-contain`}
            onError={() => handleImageError(tile.id)}
          />
        );
      }
    }

    // Priority 4: Fallback globe icon
    return (
      <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center shadow-md">
        <Globe className="w-6 h-6 text-teal-600" />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-coral-50 to-peach-100" style={{ background: 'linear-gradient(to bottom right, #fed7aa, #fecaca, #fde68a)' }}>
      <div className="max-w-6xl mx-auto px-8 py-16">
        {/* Header */}
        <div className="mb-8 md:mb-16 text-center">
          <div className="flex items-center justify-center gap-2 md:gap-8 mb-2 md:mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_link-hub-18/artifacts/ngxvvmbd_BRACE%20Logo%20Mar%2012%202025.png" 
              alt="BRACE Logo Left" 
              className="w-12 h-12 md:w-24 md:h-24 object-contain"
            />
            <h1 className="text-3xl md:text-6xl font-bold text-slate-800">
              BRACE Dashboard
            </h1>
            <img 
              src="https://customer-assets.emergentagent.com/job_link-hub-18/artifacts/mo7jmy91_BRACE%20Logo%202%20%281%29.png" 
              alt="BRACE Logo Right" 
              className="w-12 h-12 md:w-24 md:h-24 object-contain"
            />
          </div>
          <p className="text-sm md:text-xl text-slate-600 mb-4">
            Quick access to the essential BRACE links
          </p>
          
          {/* Notification Toggle */}
          <div className="max-w-md mx-auto mt-6">
            <PushNotificationToggle />
          </div>
        </div>

        {/* Tiles Grid - 3 Columns on all devices */}
        <div className="grid grid-cols-3 gap-4 md:gap-8" style={{ gridAutoFlow: 'row dense' }}>
          {dashboardTiles.map((tile, index) => {
            const isLastTile = index === dashboardTiles.length - 1;
            const isOddLastRow = dashboardTiles.length % 3 === 1;
            
            return (
              <Card
                key={tile.id}
                onClick={() => handleTileClick(tile.url)}
                className={`group relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 bg-gradient-to-br from-teal-500 to-cyan-600 border-2 border-teal-600 hover:border-teal-400 ${isLastTile && isOddLastRow ? 'md:col-start-2' : ''}`}
              >
                <div className="p-4 md:p-8 flex flex-col items-center justify-center text-center min-h-[160px] md:min-h-[180px]">
                  <div className="mb-4">
                    {renderIcon(tile)}
                  </div>
                  
                  <h3 className="text-sm md:text-lg font-semibold text-white group-hover:text-teal-50 transition-colors duration-300 leading-tight">
                    {tile.title}
                  </h3>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;